/**
 * Intelligent Data Cache Service
 * Provides caching with TTL, LRU eviction, and smart invalidation
 */

class DataCache {
    constructor() {
        this.cache = new Map();
        this.accessOrder = new Map(); // For LRU tracking
        this.maxSize = 100; // Maximum cache entries
        this.defaultTTL = 30 * 60 * 1000; // 30 minutes default
        
        // TTL strategies by data type (in milliseconds)
        this.ttlStrategies = {
            'company_profile': 60 * 60 * 1000,     // 1 hour - relatively static
            'company_logo': 24 * 60 * 60 * 1000,   // 24 hours - very static
            'related_items': 30 * 60 * 1000,       // 30 minutes - moderately dynamic
            'news': 15 * 60 * 1000,                // 15 minutes - highly dynamic
            'analyst_expectations': 60 * 60 * 1000, // 1 hour - updated periodically
            'analyst_sentiment': 60 * 60 * 1000,    // 1 hour - updated periodically
            'revenue_segmentation': 60 * 60 * 1000, // 1 hour - updated periodically
            'keyword_definition': 7 * 24 * 60 * 60 * 1000, // 7 days - very static
        };
        
        // Start cleanup interval
        this.startCleanupInterval();
        
        console.log('🗄️ DataCache initialized with max size:', this.maxSize);
    }

    /**
     * Generate cache key for different data types
     */
    generateKey(type, identifier, params = {}) {
        const baseKey = `${type}_${identifier}`;
        
        // Add parameters for news (time-sensitive)
        if (type === 'news' && params.timestamp) {
            // Round to nearest hour for news caching
            const hourlyTimestamp = Math.floor(params.timestamp / (60 * 60 * 1000)) * (60 * 60 * 1000);
            return `${baseKey}_${hourlyTimestamp}`;
        }
        
        // Add scope for analyst expectations
        if (type === 'analyst_expectations' && params.scope) {
            return `${baseKey}_${params.scope}`;
        }
        
        return baseKey;
    }

    /**
     * Get data from cache
     */
    get(key) {
        const entry = this.cache.get(key);
        
        if (!entry) {
            console.log('🗄️ Cache MISS:', key);
            return null;
        }
        
        // Check if expired
        if (Date.now() > entry.expiresAt) {
            console.log('🗄️ Cache EXPIRED:', key);
            this.cache.delete(key);
            this.accessOrder.delete(key);
            return null;
        }
        
        // Update access order for LRU
        this.accessOrder.set(key, Date.now());
        
        console.log('🗄️ Cache HIT:', key, '(expires in', Math.round((entry.expiresAt - Date.now()) / 1000 / 60), 'min)');
        return entry.data;
    }

    /**
     * Set data in cache
     */
    set(key, data, customTTL = null) {
        // Determine TTL based on data type
        const dataType = key.split('_')[0];
        const ttl = customTTL || this.ttlStrategies[dataType] || this.defaultTTL;
        
        const entry = {
            data,
            timestamp: Date.now(),
            expiresAt: Date.now() + ttl,
            ttl
        };
        
        // Check if we need to evict entries
        if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
            this.evictLRU();
        }
        
        this.cache.set(key, entry);
        this.accessOrder.set(key, Date.now());
        
        console.log('🗄️ Cache SET:', key, '(TTL:', Math.round(ttl / 1000 / 60), 'min, size:', this.cache.size + ')');
    }

    /**
     * Check if data exists and is valid
     */
    has(key) {
        const entry = this.cache.get(key);
        if (!entry) return false;
        
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            this.accessOrder.delete(key);
            return false;
        }
        
        return true;
    }

    /**
     * Invalidate specific cache entries
     */
    invalidate(pattern) {
        let count = 0;
        for (const key of this.cache.keys()) {
            if (key.includes(pattern)) {
                this.cache.delete(key);
                this.accessOrder.delete(key);
                count++;
            }
        }
        console.log('🗄️ Cache INVALIDATED:', pattern, '(' + count + ' entries)');
    }

    /**
     * Clear all cache
     */
    clear() {
        const size = this.cache.size;
        this.cache.clear();
        this.accessOrder.clear();
        console.log('🗄️ Cache CLEARED:', size, 'entries removed');
    }

    /**
     * Evict least recently used entry
     */
    evictLRU() {
        if (this.accessOrder.size === 0) return;
        
        // Find the oldest accessed entry
        let oldestKey = null;
        let oldestTime = Infinity;
        
        for (const [key, accessTime] of this.accessOrder) {
            if (accessTime < oldestTime) {
                oldestTime = accessTime;
                oldestKey = key;
            }
        }
        
        if (oldestKey) {
            this.cache.delete(oldestKey);
            this.accessOrder.delete(oldestKey);
            console.log('🗄️ Cache LRU EVICTED:', oldestKey);
        }
    }

    /**
     * Clean up expired entries
     */
    cleanup() {
        const now = Date.now();
        let cleaned = 0;
        
        for (const [key, entry] of this.cache) {
            if (now > entry.expiresAt) {
                this.cache.delete(key);
                this.accessOrder.delete(key);
                cleaned++;
            }
        }
        
        if (cleaned > 0) {
            console.log('🗄️ Cache CLEANUP:', cleaned, 'expired entries removed');
        }
    }

    /**
     * Start periodic cleanup
     */
    startCleanupInterval() {
        // Clean up every 5 minutes
        this.cleanupInterval = setInterval(() => {
            this.cleanup();
        }, 5 * 60 * 1000);
    }

    /**
     * Stop cleanup interval
     */
    destroy() {
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
        }
        this.clear();
    }

    /**
     * Get cache statistics
     */
    getStats() {
        const now = Date.now();
        let expired = 0;
        let valid = 0;
        
        for (const entry of this.cache.values()) {
            if (now > entry.expiresAt) {
                expired++;
            } else {
                valid++;
            }
        }
        
        return {
            total: this.cache.size,
            valid,
            expired,
            maxSize: this.maxSize,
            hitRate: this.hitRate || 0
        };
    }

    /**
     * Cached fetch wrapper
     */
    async cachedFetch(key, fetchFunction, customTTL = null) {
        // Try to get from cache first
        const cached = this.get(key);
        if (cached !== null) {
            return cached;
        }
        
        // Fetch fresh data
        try {
            const data = await fetchFunction();
            this.set(key, data, customTTL);
            return data;
        } catch (error) {
            console.error('🗄️ Cache fetch error for key:', key, error);
            throw error;
        }
    }
}

// Create singleton instance
const dataCache = new DataCache();

export default dataCache;
